import styled from '@emotion/styled';
import { H2Medium, H4, Subhead2 } from '@ep/components/Typography';
import { Body, Label } from '@ep/templates/ProductPage';
import theme from '@ep/theme';
import { Gcms_RichText } from '@ep/types/gatsbyTypes';
import getHtmlProp from '@ep/utils/getHtmlProp';
import intToString from '@ep/utils/intToString';
import React, { useEffect, useState } from 'react';
import Fade from 'react-reveal';

export interface StatProps {
  value: string;
  description: Gcms_RichText;
  prefixUnit: string;
  initialStat: number;
  baseStat: number;
  unit: string;
  unitSuffix?: string;
  updateValue: number;
  updatedAt: string;
}

export interface DiffDaysProp {
  date: Date;
  otherDate: Date;
}

const Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const StyledLabel = styled(H2Medium)`
  font-family: ${theme.typography.primaryFont.light};
  min-width: 16rem;
  color: ${theme.colors.duskBlue};
  text-transform: none;
  margin: 0;
`;

const Subheading = styled.div`
  min-width: 1rem;
  max-width: 15rem;
  margin: 0.5rem auto;
  color: ${theme.colors.darkBlue};
`;

function Feature({
  value,
  description,
  prefixUnit,
  initialStat,
  baseStat,
  unit,
  unitSuffix,
  updateValue,
  updatedAt,
}: StatProps) {
  const [number, setNumber] = useState(0);
  const [countAnimation, setCountAnimation] = useState(false);
  const isAbbreviation =
    unit === 'K' || unit === 'M' || unit === 'B' || unit === 'T';
  const hasValue = Boolean(initialStat);
  const hasDescription = Boolean(description);

  const diffDays = ({ date, otherDate }: DiffDaysProp) =>
    Math.ceil(Math.abs(date - otherDate) / (1000 * 60 * 60 * 24));

  const convertToRealNum = (num: number) => {
    switch (unit) {
      case 'K':
      case 'k':
        return num * 1000;
      case 'M':
      case 'm':
        return num * 1000000;
      case 'B':
      case 'b':
        return num * 1000000000;
      case 'T':
      case 't':
        return num * 1000000000000;
      default:
        return num;
    }
  };

  const updateDate = updatedAt.substring(0, 10);
  const lastUpdate = diffDays(new Date(updateDate), new Date());

  let responsiveStat = initialStat;
  if (isAbbreviation) {
    responsiveStat = convertToRealNum(initialStat);
  }

  if (lastUpdate > 7) {
    const numWeek = Math.floor(lastUpdate / 7);
    if (isAbbreviation) {
      responsiveStat = convertToRealNum(initialStat) + numWeek * updateValue;
    } else {
      responsiveStat = initialStat + numWeek * updateValue;
    }
  }

  const updatePercentage = () => {
    setTimeout(() => {
      setNumber(number + 5);
    }, 30);
  };

  const handleReveal = () => {
    setCountAnimation(true);
  };

  useEffect(() => {
    if (countAnimation) {
      if (number < 100) updatePercentage();
    }
  }, [number, countAnimation]);

  return (
    <Wrapper>
      {hasValue && (
        <div>
          <StyledLabel>
            {isAbbreviation && initialStat < 10
              ? `${intToString(responsiveStat * (number / 100))}${unitSuffix ? unitSuffix : ``}`
              : `${prefixUnit ? prefixUnit : ''}${intToString(
                  Number(
                    (responsiveStat * (number / 100)).toFixed(
                      initialStat % 1 === 0 ? 0 : 1,
                    ),
                  ),
                )}${unit === '%' ? '%' : ``}${unitSuffix ? unitSuffix : ``}`}
          </StyledLabel>
          <Fade onReveal={() => handleReveal()} />
        </div>
      )}
      {hasDescription && (
        <Subheading>
          <Body {...getHtmlProp(description.html)} />
        </Subheading>
      )}
    </Wrapper>
  );
}

export default Feature;
